import { action, extend, types } from "@gadgetinc/mobx-quick-tree";
import { isNil, upperFirst } from "lodash";
import { camelize, humanize, underscore } from "superflected";
import type { Constructor } from "type-fest";
import { slugnderscore } from "./stringUtils";

export const nameToApiIdentifier = (name: string) => camelize(slugnderscore(name), false);
export const nameFromApiIdentifier = (apiIdentifier: string) => humanize(underscore(apiIdentifier));
export const keyFromApiIdentifier = (apiIdentifier: string) => underscore(apiIdentifier).split("_").map(upperFirst).join("-");

export const hasStoredDefaultApiIdentifier = (stored: { name: string; apiIdentifier: string }) =>
  nameToApiIdentifier(stored.name) == stored.apiIdentifier;

export const makeApiIdentifier = (name: string, customApiIdentifier?: string | null) => {
  if (!isNil(customApiIdentifier)) {
    return customApiIdentifier;
  } else {
    return nameToApiIdentifier(name);
  }
};

export const ApiIdentifiedByName = types
  .model({
    customApiIdentifier: types.maybeNull(types.string),
  })
  .views((self) => ({
    get apiIdentifier() {
      return makeApiIdentifier((self as any).name, self.customApiIdentifier);
    },
  }))
  .actions((self) => ({
    setApiIdentifier(identifier: string | null) {
      self.customApiIdentifier = identifier;
    },
  }));

/**
 * Class wrapper to produce a field config type that can be marked as required or not, and then knows how to compute the storage and transit types given a presentation type based on the requiredness
 **/
export const ApiIdentifiedByNameClass = <T extends Constructor<{ name: string }>>(Klass: T) => {
  class ApiIdentified extends extend(Klass, { customApiIdentifier: types.maybeNull(types.string) }) {
    get apiIdentifier() {
      return makeApiIdentifier(this.name, this.customApiIdentifier);
    }

    @action
    setApiIdentifier(identifier: string | null) {
      this.customApiIdentifier = identifier;
    }
  }

  return ApiIdentified;
};
